import type { ButtonProps } from '~/types/button'
import { forwardRef } from 'react'
import type { LegacyRef } from 'react'
import classNames from 'classnames'

export const Button = forwardRef(function Button(
  {
    iconRight,
    iconLeft,
    text,
    onClick,
    className,
    type = 'button',
    form = '',
    size = 's',
    color = 'blue',
    shadow = false,
    loading = false,
    disabled = false,
    href,
    target = '_self',
    children = undefined,
    style = undefined,
  }: ButtonProps,
  ref: LegacyRef<HTMLButtonElement> | undefined,
): JSX.Element {
  const defaultStyle =
    'inline-flex w-fit items-center text-s gap-x-2 rounded-full whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none transition-colors duration-[175ms] ease-linear text-wrap'
  const selectedSize = {
    xs: 'h-8 px-2 lg:h-9 lg:px-3',
    s: 'h-12 px-4 lg:h-13 lg:px-5',
    m: 'h-13 px-5 lg:h-[60px]',
    l: 'h-[60px] px-5 sm:h-[68px] lg:h-[80px] lg:px-8',
  }
  const selectedStyle = {
    button: {
      blue: 'text-white bg-blue-50 hover:bg-blue-60 active:bg-blue-60',
      lightBlue: 'text-blue-50 bg-blue-10 hover:bg-blue-20 active:bg-blue-20',
      lightGray: 'text-neutral-60 bg-neutral-5 hover:bg-neutral-10 active:bg-neutral-10',
      white:
        'text-neutral-100 bg-white border border-neutral-15 hover:bg-neutral-5 active:bg-neutral-5',
      transparent: 'text-neutral-100 hover:bg-neutral-5 active:bg-neutral-5',
      dark: 'text-white bg-neutral-90 hover:bg-neutral-80 active:bg-neutral-80',
      link: '',
      green: '',
      gray: 'bg-neutral-20 text-neutral-50',
    },
    link: {
      blue: 'text-white bg-blue-50 hover:bg-blue-60 active:bg-blue-60',
      lightBlue: 'text-blue-50 bg-blue-10 hover:bg-blue-20 active:bg-blue-20',
      lightGray: 'text-neutral-60 bg-neutral-5 hover:bg-neutral-10 active:bg-neutral-10',
      white:
        'text-neutral-100 bg-white border border-neutral-15 hover:bg-neutral-5 active:bg-neutral-5',
      transparent: 'text-neutral-100 hover:bg-neutral-5 active:bg-neutral-5',
      dark: 'text-white bg-neutral-90 hover:bg-neutral-80 active:bg-neutral-80',
      link: 'text-blue-50 hover:text-blue-60 active:text-blue-60',
      green: '',
      gray: '',
    },
  }

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => {
    if (type !== 'link') event.preventDefault()
    if (onClick) onClick(event)
  }

  return (
    <>
      {type === 'link' ? (
        <a
          type={type}
          target={target}
          className={classNames(
            selectedStyle['link'][color],
            selectedSize[size],
            className,
            defaultStyle,
            shadow && 'shadow-button',
          )}
          href={href || '/#'}
          onClick={handleClick}
          style={style}
        >
          {!loading && iconLeft}
          {loading && (
            <span
              className="animate-spin inline-block w-5 h-5 lg:w-6 lg:h-6 border-[3px] border-current border-t-transparent text-current rounded-full"
              role="status"
              aria-label="loading"
            />
          )}
          {children}
          {text}
          {!loading && iconRight}
        </a>
      ) : (
        <button
          type={type}
          form={form}
          disabled={disabled || loading}
          className={classNames(
            selectedStyle['button'][color],
            selectedSize[size],
            defaultStyle,
            shadow && 'shadow-button',
            className,
          )}
          onClick={handleClick}
          ref={ref}
          style={style}
        >
          {!loading && iconLeft}
          {loading && (
            <span
              className="animate-spin inline-block w-5 h-5 lg:w-6 lg:h-6 border-[3px] border-current border-t-transparent text-current rounded-full"
              role="status"
              aria-label="loading"
            />
          )}
          {children}
          {text}
          {!loading && iconRight}
        </button>
      )}
    </>
  )
})
